import * as THREE from "three";
import { CSS2DObject } from "three-stdlib";
import { getSceneState } from "../../stores/sceneStore";
import {
  getThreeGeomArgsFromMjcfSize,
  getThreeGeomScaleFromMjcfSize,
} from "../../utils/mujocoGeomSizing";
import { createManagedLabelElement, SITE_LABEL_CLASS } from "../labelUtils";

function resolveSiteDisplayColor(rawColor) {
  const fallback = { color: "#ffff00", opacity: 0.5 };
  if (typeof rawColor !== "string") return fallback;
  const value = rawColor.trim();
  if (!value) return fallback;
  const hex = value.startsWith("#") ? value.slice(1) : value;
  if (/^[0-9a-fA-F]{8}$/.test(hex)) {
    const rgbHex = `#${hex.slice(0, 6)}`;
    const alpha = Number.parseInt(hex.slice(6, 8), 16) / 255;
    return {
      color: rgbHex,
      opacity: Number.isFinite(alpha) ? Math.max(0, Math.min(1, alpha)) : 0.5,
    };
  }
  return { color: value, opacity: 0.5 };
}

function createSiteGroup({ site, siteUuid, bodyUuid, showSites }) {
  const siteGroup = new THREE.Group();
  const sitePosition = Array.isArray(site.position) ? site.position : [0, 0, 0];
  siteGroup.position.set(...sitePosition);
  const siteOrientation = Array.isArray(site.orientation) ? site.orientation : [1, 0, 0, 0];
  siteGroup.setRotationFromQuaternion(
    new THREE.Quaternion(
      siteOrientation[1],
      siteOrientation[2],
      siteOrientation[3],
      siteOrientation[0]
    )
  );
  siteGroup.visible = true;

  const siteSize = Array.isArray(site.size) ? site.size : [0.02, 0.02, 0.02];
  let geometry;
  switch (site.kind) {
    case "sphere":
      geometry = new THREE.SphereGeometry(...getThreeGeomArgsFromMjcfSize("sphere", siteSize));
      break;
    case "box":
      geometry = new THREE.BoxGeometry(...getThreeGeomArgsFromMjcfSize("box", siteSize));
      break;
    case "cylinder":
      geometry = new THREE.CylinderGeometry(...getThreeGeomArgsFromMjcfSize("cylinder", siteSize));
      break;
    case "capsule":
      geometry = new THREE.CapsuleGeometry(...getThreeGeomArgsFromMjcfSize("capsule", siteSize));
      break;
    case "ellipsoid":
      geometry = new THREE.SphereGeometry(...getThreeGeomArgsFromMjcfSize("ellipsoid", siteSize));
      break;
    default:
      geometry = new THREE.SphereGeometry(0.02);
  }

  const siteDisplayColor = resolveSiteDisplayColor(site.color);
  const siteMaterial = new THREE.MeshBasicMaterial({
    color: siteDisplayColor.color,
    transparent: true,
    opacity: siteDisplayColor.opacity,
    side: THREE.DoubleSide,
  });

  const siteMesh = new THREE.Mesh(geometry, siteMaterial);
  if (site.kind === "ellipsoid") {
    siteMesh.scale.set(...getThreeGeomScaleFromMjcfSize("ellipsoid", siteSize));
  }
  siteMesh.visible = showSites;
  siteMesh.userData = {
    ...(siteMesh.userData || {}),
    type: "site",
    siteUuid,
    siteName: site.name,
    bodyUuid,
  };
  siteGroup.add(siteMesh);

  siteGroup.userData = {
    ...(siteGroup.userData || {}),
    type: "site",
    siteUuid,
    siteName: site.name,
    bodyUuid,
  };

  if (site.name) {
    const labelDiv = createManagedLabelElement({
      className: SITE_LABEL_CLASS,
      text: site.name,
      onClick: (e) => {
        e.stopPropagation();
        window.dispatchEvent(
          new CustomEvent("site-label-click", {
            detail: { siteUuid, siteName: site.name },
          })
        );
      },
    });

    const label = new CSS2DObject(labelDiv);
    label.position.set(0, 0.05, 0);
    label.visible = showSites;
    label.userData = {
      ...(label.userData || {}),
      type: "site",
      siteUuid,
      siteName: site.name,
      bodyUuid,
    };
    siteGroup.add(label);
    siteGroup.userData.siteLabel = label;
  }

  return siteGroup;
}

export function attachBodySites({ bodyNode, poseGroup, siteLabelsRef, showSites }) {
  if (!Array.isArray(bodyNode.sites) || bodyNode.sites.length === 0) return;

  const siteMap = getSceneState().sites;
  bodyNode.sites.forEach((siteUuid) => {
    const site = siteMap[siteUuid];
    if (!site?.name) return;

    const siteGroup = createSiteGroup({
      site,
      siteUuid,
      bodyUuid: bodyNode.uuid,
      showSites,
    });
    poseGroup.add(siteGroup);
    if (siteGroup.userData.siteLabel && site.name) {
      siteLabelsRef.current[site.name] = siteGroup.userData.siteLabel;
    }
  });
}

/**
 * Sites stored in `state.sites` but not listed on any body's `sites` array (e.g. worldbody sites
 * with no kinematic body in the scene yet). Render them directly under modelGroup in world frame.
 */
export function attachUnparentedSitesToModelGroup(modelGroup, options) {
  if (!modelGroup || !options || typeof options !== "object") return;
  const { siteLabelsRef, showSites } = options;
  const state = getSceneState();
  const bodies = state.bodies || {};
  const siteMap = state.sites || {};
  const attached = new Set();
  Object.values(bodies).forEach((body) => {
    if (!body || !Array.isArray(body.sites)) return;
    body.sites.forEach((ref) => {
      const id = typeof ref === "string" ? ref : ref?.uuid;
      if (id) attached.add(String(id));
    });
  });

  Object.entries(siteMap).forEach(([siteUuid, site]) => {
    if (!site?.name) return;
    const uuid = String(site.uuid || siteUuid);
    if (attached.has(uuid)) return;

    const siteGroup = createSiteGroup({
      site,
      siteUuid: uuid,
      bodyUuid: null,
      showSites,
    });
    modelGroup.add(siteGroup);
    if (siteLabelsRef?.current && siteGroup.userData.siteLabel && site.name) {
      siteLabelsRef.current[site.name] = siteGroup.userData.siteLabel;
    }
  });
}
